import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { tokens } from '@/theme'
import {
  editInitiativeSchema,
  type EditInitiativeFormValues,
  type EditInitiativeParsed,
} from '@/domain/editInitiativeSchema'
import type { Initiative, LookupUser } from '@/domain/types'
import { forwardGateCode } from '@/domain/funnelGates'
import { useLookupUsers } from '@/api/hooks/useLookupUsers'
import { useLookupBlockedReasons, useLookupBusinessUnits } from '@/api/hooks/useLookups'
import { useUpdateInitiative, type UpdateInitiativeBody } from '@/api/hooks/useUpdateInitiative'
import { ApiError } from '@/api/client'
import { Modal } from '@/components/Modal'

interface Props {
  open: boolean
  initiative: Initiative
  onClose: () => void
  onUpdated: (initiative: Initiative) => void
}

/**
 * Edit the funnel record.
 *
 * Identity and the blocked axis are always here. The gate groups are not:
 * only the gate the initiative is standing in front of gets its fields, so
 * an initiative at Pilot opens a form about Gate 3 rather than a form about
 * all five gates with four of them irrelevant. Nothing is lost by moving on
 * — the fields keep their values, they just stop being asked for.
 *
 * What is not here, and why:
 *   - **Stage.** Moves only through a gate. A stage dropdown would be an
 *     input that exists to be rejected with `STAGE_CHANGE_REQUIRES_GATE`.
 *   - **Closure.** Written by a Close or Salvage outcome, so the decision
 *     lands on the append-only log with a name against it.
 *   - **Capability.** Rare enough to leave to the agent's
 *     `update_initiative`, and re-doing the cascading picker here is not
 *     worth it. (Business unit *is* editable — it decides who approves at
 *     Gate 1, so a wrong one has to be fixable.)
 *   - **The shaping fields** — baseline, AI Act class, governance note,
 *     tracker, cohort, disclosure text, target date. Chat writes those; see
 *     `domain/editInitiativeSchema.ts`.
 *
 * Submit sends only what changed. Sending unchanged fields would write a
 * temporal row with no diff, which shows up in the activity feed as an
 * empty "edited" entry.
 */
export function EditInitiativeForm({ open, initiative, onClose, onUpdated }: Props) {
  const usersQuery = useLookupUsers()
  const businessUnits = useLookupBusinessUnits()
  const blockedReasons = useLookupBlockedReasons()
  const update = useUpdateInitiative()

  /** The gate this initiative is standing in front of — which group shows. */
  const pendingGate = forwardGateCode(initiative.stageCode)

  const defaultValues = useMemo<EditInitiativeFormValues>(
    () => ({
      initiativeName: initiative.initiativeName,
      initiativeDescription: initiative.initiativeDescription,
      businessUnitCode: initiative.businessUnitCode,
      ownerObjectId: initiative.ownerObjectId,
      blockedReasonCode: initiative.blockedReasonCode ?? '',
      blockedNote: initiative.blockedNote ?? '',
      successMeasure: initiative.successMeasure ?? '',
      dataRequirements: initiative.dataRequirements ?? '',
      // Never pre-ticked. This is the field Gate 3 compares against the
      // Gate 1 clear; defaulting it on would re-confirm the note every
      // time somebody fixed a typo elsewhere on the form.
      confirmGovernanceNote: false,
      result: initiative.result ?? '',
      costEnvelope: initiative.costEnvelope ?? '',
      runOwnerConfirmed: initiative.runOwnerConfirmed,
      monitoringUrl: initiative.monitoringUrl ?? '',
      runbookUrl: initiative.runbookUrl ?? '',
    }),
    [initiative],
  )

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<EditInitiativeFormValues, unknown, EditInitiativeParsed>({
    resolver: zodResolver(editInitiativeSchema),
    defaultValues,
  })

  // Reset when the modal opens or the initiative changes.
  //
  // Critical: do NOT depend on `update`. The mutation hook returns a fresh
  // object every render, so depending on it fires this effect on every
  // render and wipes whatever the user just typed. `reset` and
  // `update.reset` are stable, so omitting them is safe.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (open) {
      reset(defaultValues)
      update.reset()
    }
  }, [open, defaultValues])

  const handleClose = (): void => {
    if (isSubmitting) return
    reset(defaultValues)
    update.reset()
    onClose()
  }

  const onSubmit = async (parsed: EditInitiativeParsed): Promise<void> => {
    const body: UpdateInitiativeBody = {}

    if (parsed.initiativeName !== initiative.initiativeName) {
      body.initiativeName = parsed.initiativeName
    }
    if (parsed.initiativeDescription !== initiative.initiativeDescription) {
      body.initiativeDescription = parsed.initiativeDescription
    }
    if (parsed.businessUnitCode !== initiative.businessUnitCode) {
      body.businessUnitCode = parsed.businessUnitCode
    }
    if (parsed.ownerObjectId !== initiative.ownerObjectId) {
      body.ownerObjectId = parsed.ownerObjectId
    }
    if (parsed.runOwnerConfirmed !== initiative.runOwnerConfirmed) {
      body.runOwnerConfirmed = parsed.runOwnerConfirmed
    }
    if (parsed.confirmGovernanceNote) {
      body.confirmGovernanceNote = true
    }

    diffNullable('blockedReasonCode', initiative.blockedReasonCode, parsed.blockedReasonCode, body)
    diffNullable('blockedNote', initiative.blockedNote, parsed.blockedNote, body)
    diffNullable('successMeasure', initiative.successMeasure, parsed.successMeasure, body)
    diffNullable('dataRequirements', initiative.dataRequirements, parsed.dataRequirements, body)
    diffNullable('result', initiative.result, parsed.result, body)
    diffNullable('costEnvelope', initiative.costEnvelope, parsed.costEnvelope, body)
    diffNullable('monitoringUrl', initiative.monitoringUrl, parsed.monitoringUrl, body)
    diffNullable('runbookUrl', initiative.runbookUrl, parsed.runbookUrl, body)

    if (Object.keys(body).length === 0) {
      handleClose()
      return
    }

    const result = await update.mutate(initiative.initiativeId, body)
    if (!result) return
    onUpdated(result)
    reset(defaultValues)
    update.reset()
  }

  const apiPanelError =
    update.state.status === 'error' ? (update.state.error as ApiError | Error) : null

  return (
    <Modal open={open} onClose={handleClose} eyebrow="Edit" title="Funnel record" maxWidth={720}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        aria-busy={isSubmitting}
        style={{ display: 'grid', gap: 18 }}
      >
        {apiPanelError && <ApiPanelError error={apiPanelError} />}

        <Group label="Identity">
          <Field label="Name" error={errors.initiativeName?.message} required>
            <input type="text" {...register('initiativeName')} style={inputStyle} />
          </Field>
          <Field label="Description" error={errors.initiativeDescription?.message} required>
            <textarea
              {...register('initiativeDescription')}
              rows={4}
              style={{ ...inputStyle, resize: 'vertical', fontFamily: tokens.fontDisplay }}
            />
          </Field>
          <Two>
            <Field label="Business unit" error={errors.businessUnitCode?.message} required>
              <select {...register('businessUnitCode')} style={inputStyle}>
                {(businessUnits.status === 'success' ? businessUnits.data : []).map(bu => (
                  <option key={bu.businessUnitCode} value={bu.businessUnitCode}>
                    {bu.businessUnitName}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Owner" error={errors.ownerObjectId?.message} required>
              <select {...register('ownerObjectId')} style={inputStyle}>
                {buildOwnerOptions(
                  usersQuery.status === 'success' ? usersQuery.data.data : [],
                  initiative,
                ).map(({ value, label }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </Field>
          </Two>
        </Group>

        <Group label="Blocked" hint="Settable at any stage. The reason is what the board counts.">
          <Two>
            <Field label="Blocked reason" error={errors.blockedReasonCode?.message}>
              <select {...register('blockedReasonCode')} style={inputStyle}>
                <option value="">Not blocked</option>
                {(blockedReasons.status === 'success' ? blockedReasons.data : []).map(r => (
                  <option key={r.blockedReasonCode} value={r.blockedReasonCode}>
                    {r.blockedReasonName}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Blocked note" error={errors.blockedNote?.message}>
              <input type="text" {...register('blockedNote')} style={inputStyle} />
            </Field>
          </Two>
        </Group>

        {pendingGate === 'G1' && (
          <Group
            label="Gate 1 — approve to build"
            hint="What Gate 1 still asks a person for. The class, the baseline and the governance note are shaped in chat."
          >
            <Field label="Success measure" error={errors.successMeasure?.message}>
              <textarea
                {...register('successMeasure')}
                rows={2}
                style={{ ...inputStyle, resize: 'vertical', fontFamily: tokens.fontDisplay }}
              />
            </Field>
            <Field label="Data requirements" error={errors.dataRequirements?.message}>
              <textarea
                {...register('dataRequirements')}
                rows={2}
                style={{ ...inputStyle, resize: 'vertical', fontFamily: tokens.fontDisplay }}
              />
            </Field>
          </Group>
        )}

        {pendingGate === 'G3' && (
          <Group label="Gate 3 — commit to scale">
            <Field
              label="Result"
              error={errors.result?.message}
              hint="Same unit as the baseline, so the two can be compared."
            >
              <textarea
                {...register('result')}
                rows={2}
                style={{ ...inputStyle, resize: 'vertical', fontFamily: tokens.fontDisplay }}
              />
            </Field>
            <Field
              label="Cost envelope"
              error={errors.costEnvelope?.message}
              hint="Projected run cost. References the FinOps register rather than restating it."
            >
              <textarea
                {...register('costEnvelope')}
                rows={2}
                style={{ ...inputStyle, resize: 'vertical', fontFamily: tokens.fontDisplay }}
              />
            </Field>
            <CheckField label="Run owner confirmed" {...register('runOwnerConfirmed')} />
            {/* A Gate 3 criterion, not a Gate 1 one: the note has to have
                been looked at again *since* Gate 1 cleared. */}
            <CheckField
              label="Re-confirm the governance note now"
              hint={`Stamps today's date. Gate 3 checks this happened after Gate 1 cleared.${
                initiative.governanceNoteConfirmedAt
                  ? ` Last re-confirmed ${new Date(initiative.governanceNoteConfirmedAt).toLocaleDateString()}.`
                  : ' Never re-confirmed.'
              }`}
              {...register('confirmGovernanceNote')}
            />
          </Group>
        )}

        {pendingGate === 'G4' && (
          <Group label="Gate 4 — handover to run">
            <CheckField
              label="Run owner confirmed"
              hint="Gate 4 reads this as “the funded business owner for run”."
              {...register('runOwnerConfirmed')}
            />
            <Two>
              <Field label="Monitoring URL" error={errors.monitoringUrl?.message}>
                <input type="url" {...register('monitoringUrl')} style={inputStyle} />
              </Field>
              <Field label="Runbook URL" error={errors.runbookUrl?.message}>
                <input type="url" {...register('runbookUrl')} style={inputStyle} />
              </Field>
            </Two>
          </Group>
        )}

        {/* Sticky, because this form is taller than a laptop viewport with
            the description and two textareas open, and a save button you
            have to go looking for is a save button people stop trusting.
            The modal's backdrop is the scroll container, so `bottom: 0`
            pins the bar to the bottom of whatever is on screen. */}
        <div
          style={{
            position: 'sticky',
            bottom: 0,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 12,
            margin: '0 -28px -20px',
            padding: '14px 28px 20px',
            background: tokens.paper,
            borderTop: `1px solid ${tokens.line}`,
          }}
        >
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            style={secondaryButtonStyle}
          >
            Cancel
          </button>
          <button type="submit" disabled={isSubmitting} style={primaryButtonStyle}>
            {isSubmitting ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Emit a field only when it actually changed, mapping the form's empty
 * string onto the wire's `null` (explicit clear).
 */
function diffNullable(
  key: keyof UpdateInitiativeBody,
  currentValue: string | null,
  parsedValue: string | undefined,
  body: UpdateInitiativeBody,
): void {
  const current = currentValue ?? ''
  const next = parsedValue ?? ''
  if (next === current)
    return // The typed union on UpdateInitiativeBody is per-key; every key routed
    // through here is `string | null | undefined`, so the assertion is
    // narrowing to what the caller already guaranteed.
  ;(body as Record<string, string | null>)[key] = next.length > 0 ? next : null
}

function buildOwnerOptions(
  users: LookupUser[],
  initiative: Initiative,
): { value: string; label: string }[] {
  // The current owner is always first, even if they have paginated out of
  // the lookup or been deactivated — otherwise the select would silently
  // re-point the initiative at whoever happens to sort first.
  const out = [
    {
      value: initiative.ownerObjectId,
      label: `${initiative.ownerDisplayName || initiative.ownerObjectId} (current)`,
    },
  ]
  const seen = new Set([initiative.ownerObjectId])
  for (const u of users) {
    if (seen.has(u.entraObjectId)) continue
    seen.add(u.entraObjectId)
    out.push({ value: u.entraObjectId, label: u.displayName || u.userPrincipalName })
  }
  return out
}

// ---------------------------------------------------------------------------
// Layout primitives
// ---------------------------------------------------------------------------

function Group({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <fieldset
      style={{
        border: `1px solid ${tokens.line}`,
        borderRadius: tokens.radiusMd,
        padding: '14px 16px 16px',
        margin: 0,
        display: 'grid',
        gap: 14,
      }}
    >
      <legend
        style={{
          fontSize: 10.5,
          fontWeight: tokens.weightDemi,
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          color: tokens.ink3,
          padding: '0 6px',
        }}
      >
        {label}
      </legend>
      {hint && (
        <p style={{ margin: 0, fontSize: 12, color: tokens.ink4, lineHeight: 1.5 }}>{hint}</p>
      )}
      {children}
    </fieldset>
  )
}

function Two({ children }: { children: React.ReactNode }) {
  return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>{children}</div>
}

function Field({
  label,
  error,
  hint,
  required,
  children,
}: {
  label: string
  error?: string | undefined
  hint?: string
  required?: boolean
  children: React.ReactNode
}) {
  return (
    <label style={{ display: 'grid', gap: 5 }}>
      <span
        style={{
          fontSize: 10.5,
          fontWeight: tokens.weightDemi,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: tokens.ink3,
        }}
      >
        {label}
        {required && <span style={{ color: tokens.gold, marginLeft: 4 }}>*</span>}
      </span>
      {children}
      {hint && <span style={{ fontSize: 11.5, color: tokens.ink4, lineHeight: 1.45 }}>{hint}</span>}
      {error && (
        <span style={{ fontSize: 12, color: tokens.statusRed, fontWeight: tokens.weightMedium }}>
          {error}
        </span>
      )}
    </label>
  )
}

/**
 * Checkbox field. Forwards the react-hook-form registration through to the
 * input, so `{...register('x')}` works the same way it does on the text
 * fields.
 */
const CheckField = ({
  label,
  hint,
  ...registration
}: { label: string; hint?: string } & React.InputHTMLAttributes<HTMLInputElement> &
  Record<string, unknown>) => (
  <label
    style={{
      display: 'grid',
      gridTemplateColumns: 'auto 1fr',
      gap: 10,
      alignItems: 'start',
      cursor: 'pointer',
    }}
  >
    <input type="checkbox" {...registration} style={{ marginTop: 3 }} />
    <span>
      <span style={{ fontSize: 13, color: tokens.ink2, fontWeight: tokens.weightMedium }}>
        {label}
      </span>
      {hint && (
        <span
          style={{
            display: 'block',
            fontSize: 11.5,
            color: tokens.ink4,
            lineHeight: 1.45,
            marginTop: 2,
          }}
        >
          {hint}
        </span>
      )}
    </span>
  </label>
)

const inputStyle: React.CSSProperties = {
  border: `1px solid ${tokens.line2}`,
  borderRadius: tokens.radiusMd,
  padding: '10px 12px',
  fontFamily: tokens.fontDisplay,
  fontSize: 14,
  color: tokens.ink,
  background: tokens.paper,
  width: '100%',
  boxSizing: 'border-box',
}

const primaryButtonStyle: React.CSSProperties = {
  background: tokens.gold,
  color: tokens.paper,
  border: 'none',
  borderRadius: tokens.radiusMd,
  padding: '10px 22px',
  fontFamily: tokens.fontDisplay,
  fontSize: 12,
  fontWeight: tokens.weightDemi,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  cursor: 'pointer',
}

const secondaryButtonStyle: React.CSSProperties = {
  background: 'transparent',
  color: tokens.ink2,
  border: `1px solid ${tokens.line2}`,
  borderRadius: tokens.radiusMd,
  padding: '10px 18px',
  fontFamily: tokens.fontDisplay,
  fontSize: 12,
  fontWeight: tokens.weightMedium,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  cursor: 'pointer',
}

function ApiPanelError({ error }: { error: Error }) {
  const isApi = error instanceof ApiError
  const code = isApi ? error.code : 'INTERNAL_ERROR'
  const traceId = isApi ? error.traceId : null

  // The one rejection worth explaining rather than echoing: an attempt to
  // move stage through PATCH.
  const hint =
    isApi && error.code === 'STAGE_CHANGE_REQUIRES_GATE'
      ? 'Stage moves happen at a gate, on the detail page below this form.'
      : null

  return (
    <div
      role="alert"
      style={{
        background: tokens.statusRedWash,
        border: `1px solid ${tokens.statusRedLine}`,
        borderRadius: tokens.radiusMd,
        padding: '12px 16px',
        fontSize: 13,
        color: tokens.ink2,
        lineHeight: 1.5,
      }}
    >
      <div style={{ fontWeight: tokens.weightDemi, color: tokens.statusRed }}>{code}</div>
      <div style={{ marginTop: 4 }}>{error.message}</div>
      {hint && <div style={{ marginTop: 6, fontStyle: 'italic' }}>{hint}</div>}
      {traceId && (
        <div style={{ marginTop: 6, fontSize: 11, color: tokens.ink4 }}>trace {traceId}</div>
      )}
    </div>
  )
}
